import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BriefcaseBusiness,
  CheckCircle2,
  Clock3,
  Code2,
  Compass,
  CornerDownRight,
  Cpu,
  Filter,
  Flame,
  Heart,
  Home,
  Layers,
  MessageSquare,
  Music,
  PackageCheck,
  Palette,
  RefreshCw,
  RotateCcw,
  Search,
  SlidersHorizontal,
  Sparkles,
  Star,
  UserCheck,
  UserRound,
  Video,
  Wand2,
  X,
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import logger from "../utils/logger.js";
import { toAbsoluteUrl, handleImageFallback } from "../utils/imageUtils";
import "./FreelancerFiverr.css";
import {
  formatServiceCurrency,
  getFreelancerServiceTitle,
  getFreelancerProfileReference,
  getServiceFreelancer,
  getServiceDeliveryLabel,
  listFreelancerServices,
  getFreelancerName,
} from "../api/freelancerServices";

const serviceFallbackImage =
  "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1200&q=80";

// Preset Category Chips
const CATEGORY_CHIPS = [
  { id: "", icon: "🌟", labelAr: "جميع الخدمات", labelEn: "All Services" },
  { id: "Design", icon: "🎨", labelAr: "التصميم والرسوميات", labelEn: "Graphics & Design" },
  { id: "Development", icon: "💻", labelAr: "التطوير والبرمجة", labelEn: "Web & Tech" },
  { id: "Marketing", icon: "🚀", labelAr: "التسويق الرقمي", labelEn: "Digital Marketing" },
  { id: "Writing", icon: "✍️", labelAr: "الكتابة والترجمة", labelEn: "Writing & Translation" },
  { id: "Video", icon: "🎥", labelAr: "الفيديو والأنيميشن", labelEn: "Video & Animation" },
  { id: "Audio", icon: "🎧", labelAr: "الصوت والموسيقى", labelEn: "Music & Audio" },
  { id: "AI", icon: "🤖", labelAr: "خدمات الذكاء الاصطناعي", labelEn: "AI Services" },
  { id: "Business", icon: "📊", labelAr: "الأعمال والبيانات", labelEn: "Business & Data" },
];

function getInitials(name) {
  return String(name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase() || "FL";
}

function getSellerLevelBadge(service, isAr) {
  const orders = Number(service?.orders_count || 0);
  const rating = Number(service?.average_rating || service?.avg_rating || 0);

  if (service?.is_featured || (orders >= 15 && rating >= 4.5)) {
    return {
      label: isAr ? "بائع مميز Pro" : "Pro Verified",
      badgeClass: "fv-badge-pro",
    };
  }

  if (orders >= 10 || rating >= 4.8) {
    return {
      label: isAr ? "الأعلى تقييماً" : "Top Rated Seller",
      badgeClass: "fv-badge-top",
    };
  }

  if (orders >= 3) {
    return {
      label: isAr ? "بائع مستوى 2" : "Level 2 Seller",
      badgeClass: "",
    };
  }

  return {
    label: isAr ? "بائع موثوق" : "Rising Talent",
    badgeClass: "",
  };
}

// Fiverr-Style Gig Card Component
function GigCard({ service, isAr }) {
  const [isLiked, setIsLiked] = useState(false);
  const title = getFreelancerServiceTitle(service, isAr);
  const image = toAbsoluteUrl(service?.cover_image) || serviceFallbackImage;
  const ratingValue = Number(service?.average_rating || service?.avg_rating || 4.8);
  const reviewsCount = Number(service?.reviews_count || service?.orders_count * 2 + 1 || 0);

  const serviceRef = String(service?.slug || service?.id || "").trim();
  const detailRef = serviceRef && serviceRef !== "html" ? serviceRef : service?.id;
  const freelancer = getServiceFreelancer(service);
  const profileRef = getFreelancerProfileReference(service);
  const sellerBadge = getSellerLevelBadge(service, isAr);
  const freelancerName = getFreelancerName(freelancer, isAr);
  const rawAvatar = freelancer?.avatar_url || freelancer?.avatar || freelancer?.image;
  const avatarUrl = toAbsoluteUrl(rawAvatar);
  const deliveryDays = Number(service?.delivery_days || 1);

  const handleImageError = (event) => {
    handleImageFallback(event, serviceFallbackImage);
  };

  return (
    <article className="fv-gig-card">
      <Link to={`/freelancers/${detailRef}`} className="fv-gig-card__media" aria-label={title}>
        <img src={image} alt={title} className="fv-gig-card__image" onError={handleImageError} loading="lazy" />
        <span
          className={`fv-gig-card__delivery-badge ${
            deliveryDays <= 1 ? "fv-gig-card__badge-express" : ""
          }`}
        >
          {deliveryDays <= 1 ? "⚡ 24h Express" : `⏱️ ${getServiceDeliveryLabel(service, isAr)}`}
        </span>
      </Link>

      <div className="fv-gig-card__body">
        <div className="fv-gig-card__seller">
          <div className="fv-gig-card__avatar-wrap">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt={freelancerName}
                className="fv-gig-card__avatar"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                  const fallback = e.currentTarget.parentElement?.querySelector(".fv-gig-card__avatar-fallback");
                  if (fallback) fallback.style.display = "flex";
                }}
              />
            ) : null}
            <div
              className="fv-gig-card__avatar-fallback"
              style={{ display: avatarUrl ? "none" : "flex" }}
            >
              {getInitials(freelancerName)}
            </div>
          </div>
          <div className="fv-gig-card__seller-info">
            {profileRef ? (
              <Link
                to={`/freelancers/profile/${encodeURIComponent(profileRef)}`}
                state={{ freelancer, service }}
                className="fv-gig-card__seller-name"
              >
                {freelancerName}
              </Link>
            ) : (
              <span className="fv-gig-card__seller-name">{freelancerName}</span>
            )}
            <span className="fv-gig-card__seller-level">
              {sellerBadge.badgeClass ? (
                <span className={`fv-badge ${sellerBadge.badgeClass}`}>{sellerBadge.label}</span>
              ) : (
                <span>{sellerBadge.label}</span>
              )}
            </span>
          </div>
        </div>

        <h3 className="fv-gig-card__title">
          <Link to={`/freelancers/${detailRef}`}>{title}</Link>
        </h3>

        <div className="fv-gig-card__rating-row">
          <Star size={16} className="fv-gig-card__star" />
          <span className="fv-gig-card__score">{ratingValue.toFixed(1)}</span>
          <span className="fv-gig-card__count">({reviewsCount})</span>
        </div>

        <div className="fv-gig-card__footer">
          <button
            type="button"
            className="fv-gig-card__fav-btn"
            onClick={(e) => {
              e.preventDefault();
              setIsLiked(!isLiked);
            }}
            title={isAr ? "إضافة للمفضلة" : "Save to Favorites"}
          >
            <Heart size={18} fill={isLiked ? "#ef4444" : "none"} color={isLiked ? "#ef4444" : "currentColor"} />
          </button>

          <div className="fv-gig-card__price-wrap">
            <span className="fv-gig-card__price-label">{isAr ? "تبدأ من" : "STARTING AT"}</span>
            <span className="fv-gig-card__price-value">
              {formatServiceCurrency(service?.price || 0, service?.currency || "USD")}
            </span>
          </div>
        </div>
      </div>
    </article>
  );
}

export default function FreelancerServicesPage() {
  const { lang } = useLanguage();
  const isAr = lang === "ar";

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [priceRange, setPriceRange] = useState([0, 10000]);
  const [deliveryTime, setDeliveryTime] = useState("");
  const [minRating, setMinRating] = useState(0);
  const [sortBy, setSortBy] = useState("best_selling");

  const resetFilters = () => {
    setSearch("");
    setSelectedCategory("");
    setPriceRange([0, 10000]);
    setDeliveryTime("");
    setMinRating(0);
    setSortBy("best_selling");
  };

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        setError("");
        const data = await listFreelancerServices({ cache: false });
        if (active) setServices(data);
      } catch (err) {
        logger.error("Freelancer services load error", err);
        if (active) {
          setError(isAr ? "فشل تحميل خدمات المستقلين." : "Failed to load freelancer services.");
        }
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [isAr]);

  const filteredServices = useMemo(() => {
    let result = [...services];
    const query = search.trim().toLowerCase();

    if (query) {
      result = result.filter((service) => {
        const haystack = [
          service?.title_ar,
          service?.title_en,
          service?.short_description_ar,
          service?.short_description_en,
          service?.category,
          service?.freelancer?.name,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return haystack.includes(query);
      });
    }

    if (selectedCategory) {
      result = result.filter((service) => service?.category === selectedCategory);
    }

    result = result.filter((service) => {
      const price = Number(service?.price || 0);
      return price >= priceRange[0] && price <= priceRange[1];
    });

    if (deliveryTime) {
      result = result.filter((service) => Number(service?.delivery_days || 0) <= Number(deliveryTime));
    }

    if (minRating > 0) {
      result = result.filter(
        (service) => Number(service?.average_rating || service?.avg_rating || 0) >= minRating
      );
    }

    // Sorting
    result.sort((a, b) => {
      if (sortBy === "best_selling") {
        return Number(b?.orders_count || 0) - Number(a?.orders_count || 0);
      }
      if (sortBy === "top_rated") {
        return Number(b?.average_rating || b?.avg_rating || 0) - Number(a?.average_rating || a?.avg_rating || 0);
      }
      if (sortBy === "price_asc") {
        return Number(a?.price || 0) - Number(b?.price || 0);
      }
      if (sortBy === "price_desc") {
        return Number(b?.price || 0) - Number(a?.price || 0);
      }
      if (sortBy === "newest") {
        return Number(b?.id || 0) - Number(a?.id || 0);
      }
      return 0;
    });

    return result;
  }, [services, search, selectedCategory, priceRange, deliveryTime, minRating, sortBy]);

  const hasActiveFilters =
    search.trim() ||
    selectedCategory ||
    priceRange[0] !== 0 ||
    priceRange[1] !== 10000 ||
    deliveryTime ||
    minRating > 0;

  return (
    <div className="fv-marketplace-wrapper" dir={isAr ? "rtl" : "ltr"}>
      <div className="fv-container">
        {/* Breadcrumbs */}
        <nav className="py-3 d-flex align-items-center gap-2 small text-muted">
          <Link to="/" className="text-decoration-none text-muted d-flex align-items-center gap-1">
            <Home size={14} />
            {isAr ? "الرئيسية" : "Home"}
          </Link>
          <span>/</span>
          <span className="fw-semibold text-body">{isAr ? "سوق الخدمات والعمل الحر" : "Freelance Marketplace"}</span>
        </nav>

        {/* Hero Section */}
        <section className="fv-hero">
          <div className="fv-hero__content">
            <span className="fv-hero__badge">
              <Sparkles size={15} />
              {isAr ? "منصة الخدمات الرقمية المحترفة" : "Professional Freelance Marketplace"}
            </span>
            <h1 className="fv-hero__title">
              {isAr ? (
                <>اعثر على <span>أفضل المستقلين</span> لمشروعك القادم</>
              ) : (
                <>Find the perfect <span>freelance services</span> for your business</>
              )}
            </h1>
            <p className="fv-hero__subtitle">
              {isAr
                ? "تصفح آلاف الخدمات البرمجية والتصميمية والتسويقية المنفذة بأعلى معايير الجودة والتسليم المضمون."
                : "Explore thousands of tech, design, writing & marketing services delivered by top experts with full buyer protection."}
            </p>

            {/* Search Bar */}
            <div className="fv-search-box">
              <div className="fv-search-box__input-wrap">
                <Search size={20} />
                <input
                  type="search"
                  className="fv-search-box__input"
                  placeholder={
                    isAr
                      ? "ابحث عن خدمة (مثل: تصميم موقع، كود برمجي، موشن جرافيك)..."
                      : "Search for any service (e.g. Website design, React app, Logo)..."
                  }
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <button type="button" className="fv-search-box__btn">
                <span>{isAr ? "بحث" : "Search"}</span>
                <ArrowRight size={16} className={isAr ? "rotate-180" : ""} />
              </button>
            </div>
          </div>
        </section>

        {/* Categories Chips Bar */}
        <section className="fv-categories-bar">
          <div className="fv-categories-bar__title">
            <Compass size={16} />
            {isAr ? "تصفح حسب التخصص" : "POPULAR CATEGORIES"}
          </div>
          <div className="fv-chips-scroll">
            {CATEGORY_CHIPS.map((chip) => {
              const isActive = selectedCategory === chip.id;
              return (
                <button
                  type="button"
                  key={chip.id}
                  className={`fv-chip ${isActive ? "is-active" : ""}`}
                  onClick={() => setSelectedCategory(chip.id)}
                >
                  <span className="fv-chip__icon">{chip.icon}</span>
                  <span>{isAr ? chip.labelAr : chip.labelEn}</span>
                </button>
              );
            })}
          </div>
        </section>

        {/* Advanced Filters & Sorting Sub-Bar */}
        <section className="fv-filter-bar">
          <div className="fv-filter-bar__header">
            <div className="fv-filter-bar__title">
              <SlidersHorizontal size={18} />
              <span>{isAr ? "خيارات الفلترة والفرز" : "Filter & Sort Options"}</span>
            </div>

            {hasActiveFilters ? (
              <button type="button" className="fv-filter-bar__reset" onClick={resetFilters}>
                <RotateCcw size={14} />
                {isAr ? "إعادة ضبط الفلاتر" : "Clear All Filters"}
              </button>
            ) : null}
          </div>

          <div className="fv-filter-bar__grid">
            {/* Price Filter */}
            <div className="fv-filter-group">
              <label>{isAr ? "نطاق السعر ($)" : "Budget ($)"}</label>
              <div className="fv-price-split">
                <input
                  type="number"
                  className="fv-filter-input"
                  placeholder={isAr ? "أدنى" : "Min"}
                  value={priceRange[0] === 0 ? "" : priceRange[0]}
                  onChange={(e) => setPriceRange([Number(e.target.value || 0), priceRange[1]])}
                />
                <input
                  type="number"
                  className="fv-filter-input"
                  placeholder={isAr ? "أقصى" : "Max"}
                  value={priceRange[1] === 10000 ? "" : priceRange[1]}
                  onChange={(e) => setPriceRange([priceRange[0], Number(e.target.value || 10000)])}
                />
              </div>
            </div>

            {/* Delivery Time */}
            <div className="fv-filter-group">
              <label>{isAr ? "وقت التسليم" : "Delivery Time"}</label>
              <select
                className="fv-filter-select"
                value={deliveryTime}
                onChange={(e) => setDeliveryTime(e.target.value)}
              >
                <option value="">{isAr ? "أي وقت" : "Anytime"}</option>
                <option value="1">{isAr ? "سريع جدًا (خلال 24 ساعة)" : "Express (Up to 24h)"}</option>
                <option value="3">{isAr ? "حتى 3 أيام" : "Up to 3 days"}</option>
                <option value="7">{isAr ? "حتى 7 أيام" : "Up to 7 days"}</option>
              </select>
            </div>

            {/* Rating Filter */}
            <div className="fv-filter-group">
              <label>{isAr ? "التقييم الأدنى" : "Min Rating"}</label>
              <select
                className="fv-filter-select"
                value={minRating}
                onChange={(e) => setMinRating(Number(e.target.value))}
              >
                <option value="0">{isAr ? "جميع التقييمات" : "All Ratings"}</option>
                <option value="4.5">★ 4.5+</option>
                <option value="4.0">★ 4.0+</option>
                <option value="3.5">★ 3.5+</option>
              </select>
            </div>

            {/* Sort By */}
            <div className="fv-filter-group">
              <label>{isAr ? "ترتيب حسب" : "Sort By"}</label>
              <select
                className="fv-filter-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="best_selling">{isAr ? "الأكثر مبيعات (Best Selling)" : "Best Selling"}</option>
                <option value="top_rated">{isAr ? "الأعلى تقييماً (Top Rated)" : "Top Rated"}</option>
                <option value="newest">{isAr ? "الأحدث (Newest)" : "Newest Arrivals"}</option>
                <option value="price_asc">{isAr ? "السعر: من الأقل للأعلى" : "Price: Low to High"}</option>
                <option value="price_desc">{isAr ? "السعر: من الأعلى للأقل" : "Price: High to Low"}</option>
              </select>
            </div>
          </div>

          {/* Active Filter Chips */}
          {hasActiveFilters ? (
            <div className="fv-active-tags">
              <small className="text-muted fw-semibold me-2">{isAr ? "الفلاتر النشطة:" : "Active Filters:"}</small>
              {search ? (
                <span className="fv-active-tag">
                  {isAr ? `بحث: "${search}"` : `Search: "${search}"`}
                  <button type="button" className="fv-active-tag__remove" onClick={() => setSearch("")}>×</button>
                </span>
              ) : null}
              {selectedCategory ? (
                <span className="fv-active-tag">
                  {selectedCategory}
                  <button type="button" className="fv-active-tag__remove" onClick={() => setSelectedCategory("")}>×</button>
                </span>
              ) : null}
              {deliveryTime ? (
                <span className="fv-active-tag">
                  {isAr ? `خلال ${deliveryTime} يوم` : `Up to ${deliveryTime} days`}
                  <button type="button" className="fv-active-tag__remove" onClick={() => setDeliveryTime("")}>×</button>
                </span>
              ) : null}
              {minRating > 0 ? (
                <span className="fv-active-tag">
                  ★ {minRating}+
                  <button type="button" className="fv-active-tag__remove" onClick={() => setMinRating(0)}>×</button>
                </span>
              ) : null}
            </div>
          ) : null}
        </section>

        {/* Section Head */}
        <div className="fv-section-head">
          <h2 className="fv-section-head__title">
            <Flame color="#1dbf73" size={22} />
            {selectedCategory
              ? (isAr ? `خدمات ${selectedCategory}` : `${selectedCategory} Services`)
              : (isAr ? "الخدمات المتاحة" : "Available Freelance Gigs")}
          </h2>
          <span className="fv-section-head__count">
            {filteredServices.length} {isAr ? "خدمة متاحة" : "services available"}
          </span>
        </div>

        {/* Main Content Grid / States */}
        {loading ? (
          <div className="fv-services-grid">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((item) => (
              <div key={item} className="fv-skeleton-card">
                <div className="fv-skeleton-block h-100" />
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="fv-state-box">
            <div className="fv-state-box__icon">
              <RotateCcw size={32} />
            </div>
            <h3 className="fv-state-box__title">{error}</h3>
            <button type="button" className="fv-search-box__btn mx-auto mt-3" onClick={() => window.location.reload()}>
              {isAr ? "إعادة التنشيط" : "Reload"}
            </button>
          </div>
        ) : filteredServices.length === 0 ? (
          <div className="fv-state-box">
            <div className="fv-state-box__icon">
              <Search size={32} />
            </div>
            <h3 className="fv-state-box__title">
              {isAr ? "لم نجد أي خدمات تطابق فلاتر البحث" : "No services matched your criteria"}
            </h3>
            <p className="fv-state-box__desc">
              {isAr
                ? "جرب إزالة بعض الفلاتر أو استخدام كلمات بحث مختلفة للحصول على نتائج أكثر."
                : "Try adjusting your price range, search keywords or category selection."}
            </p>
            <button type="button" className="fv-search-box__btn mx-auto" onClick={resetFilters}>
              <RotateCcw size={16} />
              {isAr ? "إعادة تعيين جميع الفلاتر" : "Reset All Filters"}
            </button>
          </div>
        ) : (
          <div className="fv-services-grid">
            {filteredServices.map((service) => (
              <GigCard key={service.id} service={service} isAr={isAr} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
